import { Component, ViewChild, Output, EventEmitter } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { SettingsService } from '@shared/settings.service';
import { GlobalSettingsService } from '@core/globalSettings.service';
import {
	ExportSettings,
	CreateOrthodonticsExportFileRequestDto,
	CreateRestorativeExportFileRequestDto,
	CreateExportFilesRequestDto
} from '@shared/generalInterfaces';
import { combineLatest, Subscription } from 'rxjs';
import { GoogleAnalyticsService } from '@core/googleAnalytics.service';
import { ExportInfoDto } from '@shared/popup/popup.service';
import { DownloadNotificationService } from '@shared/downloadNotification/downloadNotification.service';
import { Order } from '../../doctors/orders/orders.service';
import { ExportPopupService } from '@shared/popup/popup.service';
import { DownloadFileInfo } from '@shared/downloadNotification/fileInfo.model';
import { ISettingsData } from '../../interfaces/ISettingsData';
import { ILabCompanySettingsDto } from '../../interfaces/ILabCompanySettingsDto';
import { SpinnerService } from '@core/spinner/spinner.service';
import { SoftwareOptionsService } from '@core/softwareOptions.service';
import { SoftwareOptionsForCompany } from '@shared/enums';

@Component({
	selector: 'eup-labExport',
	templateUrl: './labExport.component.html',
	styleUrls: [ './labExport.component.scss' ]
})
export class LabExportModal {
	private shouldSaveAsDefaultValues: boolean;

	@ViewChild('labExportModal') private labExportModal: ModalDirective;

	@Output() public downloadClicked: EventEmitter<any> = new EventEmitter();

	defaultValuesAreSet: boolean;
	isInitialize = true;
	cadCamSystemTypes: any[];
	dataFormatOptions: any[];
	exportTypeIrecordOptions: any[];
	fileTypeOptions: any[];
	hidePatientName: boolean;
	disablePatientDetailsBySWOpiont: boolean;

	orthodonticsOrders: Order[];
	restorativeOrders: Order[];
	numOfOrders = 0;
	numOfOrthodonticsCases = 0;
	numOfRestorativeCases = 0;

	defaultExportSettings: ExportSettings = {
		exportTypeIrecord: 0,
		dataFormat: 0,
		fileType: 0,
		cadCamSystemTypeId: 0
	};

	exportSettings: ExportSettings = {
		exportTypeIrecord: 0,
		dataFormat: 0,
		fileType: 0,
		cadCamSystemTypeId: 0
	};

	exportSubscription: Subscription;
	exportWoSubscription: Subscription;
	defaultLabCompanySetting: ILabCompanySettingsDto;
	tab = -1;

	private changedExportSettings: { [key: string]: boolean } = {};

	constructor(
		private settingsService: SettingsService,
		private globalSettings: GlobalSettingsService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private popupService: ExportPopupService,
		private downloadNotificationService: DownloadNotificationService,
		private spinnerService: SpinnerService,
		private softwareOptionsService: SoftwareOptionsService
	) {}

	private subscribeExportSettings(settingsRes: ISettingsData) {
		// init options
		this.exportTypeIrecordOptions = settingsRes.exportTypeIRecordOptions;
		this.dataFormatOptions = settingsRes.dataFormatOptions;
		this.fileTypeOptions = settingsRes.fileTypeOptions;
		this.cadCamSystemTypes = settingsRes.cadCamSystemTypes;
	}

	private subscribeCompanySettingsData(labCompanySettingsDto: ILabCompanySettingsDto) {
		this.exportSettings.exportTypeIrecord = this.setDefaultValue(labCompanySettingsDto.orthoExportSettings.iRecord);
		this.exportSettings.dataFormat = this.setDefaultValue(labCompanySettingsDto.orthoExportSettings.dataFormat);
		this.exportSettings.fileType = this.setDefaultValue(labCompanySettingsDto.orthoExportSettings.fileType);
		this.exportSettings.cadCamSystemTypeId = this.setDefaultValue(
			labCompanySettingsDto.restoExportSettings.cadCamSystemType
		);
		this.hidePatientName = labCompanySettingsDto.patientAnonymization;
		this.defaultLabCompanySetting = labCompanySettingsDto;
	}

	show(orders: Order[]): void {
		this.spinnerService.start();
		this.disablePatientDetailsBySWOpiont = this.softwareOptionsService.isSoftwareOptionExists(SoftwareOptionsForCompany.shouldDisableHidePatientDetails, this.globalSettings.get().companySoftwareOptions);
		const combined = combineLatest(
			this.settingsService.getSettings(this.globalSettings.get().selectedCompanyId, true, false),
			this.settingsService.getLabCompanySettings(true, false)
		);

		combined.subscribe((response: [ISettingsData, ILabCompanySettingsDto]) => {
			this.subscribeExportSettings(response[0]);
			this.subscribeCompanySettingsData(response[1]);

			this.defaultValuesAreSet = true;
			this.shouldSaveAsDefaultValues = false;
			this.orthodonticsOrders = orders.filter((o) => o.isOrthoOrder);
			this.restorativeOrders = orders.filter((o) => !o.isOrthoOrder);
			this.numOfOrthodonticsCases = this.orthodonticsOrders.length;
			this.numOfRestorativeCases = this.restorativeOrders.length;
			this.numOfOrders = this.numOfOrthodonticsCases + this.numOfRestorativeCases;

			this.tab = this.numOfRestorativeCases > 0 ? 1 : 2;
			this.labExportModal.show();
			this.spinnerService.stop();
			this.googleAnalyticsService.hitEvent('Order', 'WebExport', 'Export');
		});
	}

	hide() {
		this.labExportModal.hide();
		this.tab = -1;
	}

	saveDefaultValues() {
		if (this.restorativeOrders.length) {
			this.defaultLabCompanySetting.restoExportSettings.cadCamSystemType = this.exportSettings.cadCamSystemTypeId;
		}

		if (this.orthodonticsOrders.length) {
			this.defaultLabCompanySetting.orthoExportSettings.dataFormat = this.exportSettings.dataFormat;
			this.defaultLabCompanySetting.orthoExportSettings.fileType = this.exportSettings.fileType;
			this.defaultLabCompanySetting.orthoExportSettings.iRecord = this.exportSettings.exportTypeIrecord;
		}

		this.settingsService.setLabCompanySettings(this.defaultLabCompanySetting).subscribe(() => {});
	}

	export = () => {
		const settings = this.globalSettings.get();
		const clientId = settings.contactId;
		const map = {};

		const requestData: CreateExportFilesRequestDto = {};

		const restorative = this.restorativeOrders.map((o) => {
			const reqData: CreateRestorativeExportFileRequestDto = {
				clientId,
				orderId: o.id,
				orderDetailsId: o.lastDetailsId,
				cadCamSystemTypeId: this.exportSettings.cadCamSystemTypeId,
				patientAnonymization: this.disablePatientDetailsBySWOpiont ? false : this.hidePatientName
			};
			map[o.id] = reqData;
			return reqData;
		});

		if (restorative.length > 0) {
			requestData.restorativeExportRequest = { dtos: restorative };
		}

		const orthodontics = this.orthodonticsOrders.map((o) => {
			const reqData: CreateOrthodonticsExportFileRequestDto = {
				clientId,
				orderId: o.id,
				orderDetailsId: o.lastDetailsId,
				fileFormat: this.exportSettings.dataFormat,
				fileType: this.exportSettings.fileType,
				typeFormat: this.exportSettings.exportTypeIrecord,
				patientAnonymization: this.disablePatientDetailsBySWOpiont ? false : this.hidePatientName
			};
			map[o.id] = reqData;
			return reqData;
		});

		if (orthodontics.length > 0) {
			requestData.orthodonticsExportRequest = { dtos: orthodontics };
		}

		this.subscribeToExportInfoBulk(requestData, map);

		if (!this.defaultValuesAreSet && this.shouldSaveAsDefaultValues) {
			this.saveDefaultValues();
		}

		const result = [...this.orthodonticsOrders, ...this.restorativeOrders].map(item => item.id);
		this.downloadClicked.emit(result);
		this.hide();
	}

	private getExportInfoBulk(requestData: CreateExportFilesRequestDto, exportMap: {}) {
		this.exportSubscription = this.popupService.getExportInfoBulk(requestData).subscribe((res: ExportInfoDto[]) => {
			res.map((o) => {
				this.downloadNotificationService.addFile(o.parallelWorkOrderId, exportMap[o.orderId]);
			});
		});
	}

	private subscribeToExportInfoBulk(requestData: CreateExportFilesRequestDto, exportMap: {}) {
		// check if download file ready by ideOrder work order
		this.exportWoSubscription = this.popupService
			.getExportInfoBulkByWorkOrder(requestData)
			.subscribe((res: ExportInfoDto[]) => {
				if (!res) {
					return;
				}

				const directDownloadItems = res.filter((t) => t.workOrderId > 0);
				if (requestData.orthodonticsExportRequest) {
					requestData.orthodonticsExportRequest.dtos = requestData.orthodonticsExportRequest.dtos.filter(
						(dto) => !directDownloadItems.some((x) => +x.orderId === dto.orderId)
					);
				}
				if (requestData.restorativeExportRequest) {
					requestData.restorativeExportRequest.dtos = requestData.restorativeExportRequest.dtos.filter(
						(dto) => !directDownloadItems.some((x) => +x.orderId === dto.orderId)
					);
				}

				directDownloadItems.forEach((info) => {
					const fileInfo: DownloadFileInfo = {
						parallelWorkOrderId: -1,
						workOrderId: info.workOrderId,
						orderId: info.orderId,
						orderDetailsId: -1,
						downloaded: true
					};
					this.downloadNotificationService.downloadFile(fileInfo);
				});

				if (
					(requestData.orthodonticsExportRequest && requestData.orthodonticsExportRequest.dtos.length > 0) ||
					(requestData.restorativeExportRequest && requestData.restorativeExportRequest.dtos.length > 0)
				) {
					this.getExportInfoBulk(requestData, exportMap);
				}
			});
	}

	openTab(tab: number) {
		this.tab = tab;
	}

	onSelectExportTypeIrecord(item: any) {
		this.exportSettings.exportTypeIrecord = item.key;
		this.setDefaultValuesFlag('exportTypeIrecord', item.key !== this.defaultLabCompanySetting.orthoExportSettings.iRecord);
	}

	onSelectDataFormat(item: any) {
		this.exportSettings.dataFormat = item.key;
		this.setDefaultValuesFlag('dataFormat', item.key !== this.defaultLabCompanySetting.orthoExportSettings.dataFormat);
	}

	onSelectFileTypeOptions(item: any) {
		this.exportSettings.fileType = item.key;
		this.setDefaultValuesFlag('fileType', item.key !== this.defaultLabCompanySetting.orthoExportSettings.fileType);
	}

	onSelectCadCamSystemTypes(item: any) {
		this.exportSettings.cadCamSystemTypeId = item.key;
		this.setDefaultValuesFlag('cadCamSystemTypeId', item.key !== this.defaultLabCompanySetting.restoExportSettings.cadCamSystemType);
	}

	setDefaultValue(value: number) {
		return value === undefined || value === null ? 0 : value;
	}

	saveAsDefaultValues(evt: boolean): void {
		this.shouldSaveAsDefaultValues = evt;
	}

	setHidePatientName(event: boolean): void {
		this.hidePatientName = event;
	}

	private setDefaultValuesFlag(value: string, changed: boolean) {
		this.changedExportSettings[value] = changed;

		let hasChangedItem = false;
		for (const key in this.changedExportSettings) {
			if (this.changedExportSettings.hasOwnProperty(key)) {
				hasChangedItem = this.changedExportSettings[key];
				if (hasChangedItem) {
					break;
				}
			}
		}

		this.defaultValuesAreSet = !hasChangedItem;
	}
}
